import { Component, Input, OnInit, AfterViewInit, OnDestroy, SimpleChanges, OnChanges, ElementRef, ViewChild } from '@angular/core';
import { TagComboBoxModel } from './tag-combo-box.model';
import { KYMW68GetTagListService } from './api-services/get-tag-list.service';
import { PlessHttp } from '../../../../shared/http/pless-http.service';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { PlessViewUtilsSub } from '../../../../commons/kymw00/pless-view-utils-sub.service';
import { PlessDialogServiceSub } from '../../../../commons/kymw00/pless-dialog-service-sub';
import { Tag } from './tag.model';

/**
 * 共通部品 タグコンボボックスを制御するクラス
 *
 * @export Kymw68TagComboBoxComponent
 * @class TagComboBoxComponent
 * @implements {OnInit}
 * @implements {AfterViewInit}
 * @implements {OnChanges}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'app-kymw68-tag-combo-box',
  templateUrl: 'tag-combo-box.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: Kymw68TagComboBoxComponent,
      multi: true
    },
    {
      // データをとるサービスを複数newしたいため、こちらに記述
      provide: KYMW68GetTagListService,
      // 初期化処理を指定
      useFactory: (plessHttp: PlessHttp, dialogService: PlessDialogServiceSub) => {
        return new KYMW68GetTagListService(plessHttp, dialogService);
      },
      deps: [PlessHttp, PlessDialogServiceSub]
    },
  ],
})

export class Kymw68TagComboBoxComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {

  // タグID
  public selectedTagId: string = null;
  // タグ名
  public selectedTagName: string = <string>'';
  // タグID名
  public selectedTagIdName: string = <string>'';
  // タグ情報モデルインスタンス
  public model: TagComboBoxModel = new TagComboBoxModel();
  // 選択index
  public selectedIndex: number = <number>-1;

  @Input() public subid: string = null;
  @Input() blank: boolean = <boolean>true;
  @Input() required: boolean = <boolean>false;
  @Input() disabled: boolean = <boolean>false;
  @Input() initAutoLoad: boolean = <boolean>true;
  @Input() left: number = <number>10;
  @Input() top: number = <number>10;
  @Input() width: number | string; // どちらでも受けてTemplateに判断させる
  @Input() height: number = <number>20;
  @Input() public initFocusItem: any = null;                                    // ロード完了後フォーカスを移動させるid
  @Input() public tabindex: number = null;
  @Input() visible: boolean = <boolean>true;                                    // 表示・非表示フラグ
  @Input() public autofocus: boolean = <boolean>false;
  @Input() forcedEnabledMode: string = null;

  /** コンボボックスの参照 */
  @ViewChild('comboBox', { static: true }) comboBox: ElementRef;

  /**
   * Creates an instance of TagComboBoxComponent.
   * @param {ElementRef} elementRef
   * @param {KYMW68GetTagListService} getTagListService
   * @memberof TagComboBoxComponent
   */
  constructor(
    public elementRef: ElementRef,
    private getTagListService: KYMW68GetTagListService,
  ) { }

  // ValueAccessor
  public onChange(_: any) { }
  public onTouched() { }
  public writeValue(text: string): void {
    if (text !== this.selectedTagId) {
      this.selectedTagId = text;
      this.setTagValue(text);
    }
  }
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * 初期化処理
   *
   * @memberof Kymw68TagComboBoxComponent
   */
  ngOnInit(): void {
    // コンボボックスインスタンス設定
    this.model.component = this;
    this.comboBox.nativeElement.setAttribute('id', this.subid + '_sub');
    this.comboBox.nativeElement.setAttribute('forcedEnabledMode', this.forcedEnabledMode);
  }

  /**
   * ロード完了時
   *
   * @memberof Kymw68TagComboBoxComponent
   */
  ngAfterViewInit(): void {
    // データ取得
    this.getTagListService.execute(this.model);
  }

  /**
   * 値変更時イベント
   *
   * @param {SimpleChanges} changes 変更イベント
   * @memberof Kymw68TagComboBoxComponent
   */
  ngOnChanges(changes: SimpleChanges): void {
    Object.keys(changes).forEach((propName) => {
      switch (propName) {
        case 'blank':
          // 先頭空白表示フラグ
          this.model.blank = changes.blank.currentValue;
          break;
        case 'initAutoLoad':
          // 初期データ自動取得フラグ
          this.model.initAutoLoad = changes.initAutoLoad.currentValue;
          break;
      }
    });
  }

  /**
   * コンポーネント終了時
   *
   * @memberof Kymw68TagComboBoxComponent
   */
  ngOnDestroy(): void {
    // コンボボックス情報初期化
    this.clear();
  }

  /**
   * コンボボックスの選択タグを切り替える
   *
   * @param {string} tagId タグID
   * @memberof Kymw68TagComboBoxComponent
   */
  public setTagValue(tagId: string): void {
    this.model.setTagValue = tagId;

    // ロード完了の場合
    if (this.model.completeUpdateFlg) {
      this.updateSelectedTagId();
    }
  }

  /**
   * コンボボックスの選択コードNameを切り替える
   *
   * @memberof Kymw68TagComboBoxComponent
   */
  public updateSelectedTagId(): void {
    // let tempList: Feature[] = this.model.dataList;
    const tagList: Tag[] = this.model.tagList;
    for (let i = Number(0); i < tagList.length; i++) {
      if (PlessViewUtilsSub.trim(tagList[i].TagId) === this.model.setTagValue) {
        this.selectedIndex = i;
        this.update(null);
      }
    }

    // バインドフィールドを更新
    if (this.initFocusItem != null) {
      let queryedElement;
      if (this.initFocusItem instanceof HTMLElement) {
        queryedElement = this.initFocusItem.querySelector('textarea, input, select');
      } else {
        queryedElement = this.initFocusItem.elementRef.nativeElement.querySelector('textarea, input, select');
      }
      (queryedElement ? <HTMLElement>queryedElement : this.initFocusItem).focus();
    }
  }

  /**
   * 再ロード
   *
   * @param {*} e
   * @returns {void}
   * @memberof Kymw68TagComboBoxComponent
   */
  public update(e: any): void {

    if (e != null) {
      this.selectedIndex = e.target.selectedIndex;
    }

    if (this.selectedIndex === -1) {
      this.selectedTagId = <string>'';
      this.selectedTagName = <string>'';
      this.selectedTagIdName = <string>'';
      return;
    }

    this.selectedTagId = this.model.tagList[this.selectedIndex].TagId;
    this.selectedTagName = this.model.tagList[this.selectedIndex].TagName;
    this.selectedTagIdName = this.model.tagList[this.selectedIndex].TagIdName;
  }

  /**
   * 初期化処理
   *
   * @memberof Kymw68TagComboBoxComponent
   */
  public clear(): void {
    this.selectedTagId = <string>'';
    this.selectedTagName = <string>'';
    this.selectedTagIdName = <string>'';
    this.selectedIndex = <number>-1;
    this.model = new TagComboBoxModel();
    this.model.component = this;
    this.model.blank = this.blank;
  }

  /**
   * コンボボックスの選択Indexを切り替える
   *
   * @param {number} index
   * @memberof Kymw68TagComboBoxComponent
   */
  public setSelectedIndex(index: number): void {
    this.model.selectedIndex = index;
    // ロード完了の場合
    if (this.model.completeUpdateFlg) {
      this.updateSelectedIndex();
    }
  }

  /**
   * コンボボックスの選択Indexを切り替える
   *
   * @memberof Kymw68TagComboBoxComponent
   */
  public updateSelectedIndex(): void {
    if (this.model.selectedIndex >= 0) {
      this.selectedIndex = this.model.selectedIndex;
      this.model.selectedIndex = <number>-1;
      this.update(null);
    }
  }

  /**
   * setFocusOverride※テキスト選択解除の為
   *
   * @memberof Kymw68TagComboBoxComponent
   */
  public setFocus(): void {
    this.comboBox.nativeElement.focus();
  }

}
